import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { PencilIcon, ArrowRightIcon } from 'lucide-react';
export const ProductDescriptionsPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-5154">
      <Navbar scrolled={scrolled} data-id="element-5155" />
      <main className="pt-24 pb-20" data-id="element-5156">
        <div className="container mx-auto px-4 md:px-6" data-id="element-5157">
          <div className="max-w-4xl mx-auto" data-id="element-5158">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-5159">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-5160">
                <PencilIcon className="h-64 w-64" data-id="element-5161" />
              </div>
              <div className="relative z-10" data-id="element-5162">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5163">
                  Writing Product Listings That Convert
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-5164">
                  Discover how to create clear, compelling product titles and
                  descriptions that attract buyers and increase conversions.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-5165">
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5166">
                Your product listing is often the first impression a buyer has
                of your work. A strong listing can immediately build trust,
                communicate value, and convince someone to make a purchase.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5167">
                Start with a clear and specific title. Avoid vague names and
                instead focus on what your product actually offers. A good title
                should quickly tell the buyer what they are getting and who it
                is for.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5168">
                Your description should focus on benefits, not just features.
                Instead of listing what the product includes, explain how it
                helps the buyer. Does it save time? Improve results? Simplify a
                process? Make that clear from the beginning.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5169">
                Structure your description so it is easy to scan. Use short
                paragraphs and break down information into sections such as
                what's included, who it's for, and how to use it. Most buyers
                will skim before deciding to read more.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5170">
                Visual presentation also plays a big role. Even if your product
                is high quality, a weak description or unclear structure can
                reduce trust. Make sure everything looks organized and
                professional.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5171">
                Finally, be honest and transparent. Clearly explain what the
                buyer will receive, any requirements, and any limitations. This
                reduces confusion and builds long-term trust with your
                customers.
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-5172">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-5173">
                  A well-written product listing doesn't just describe your
                  product — it sells it.
                </p>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-5174">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5175">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5176">
                <a href="/learn/pricing-strategies" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5177">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5178">
                    How to Price Your Digital Products
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5179">
                    Learn how to choose the right pricing strategy based on
                    value, competition, and target audience.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5180">
                    <span data-id="element-5181">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5182" />
                  </div>
                </a>
                <a href="/learn/product-bundles" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5183">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5184">
                    Product Bundles That Sell
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5185">
                    Discover how to create irresistible product bundles that
                    increase your average order value.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5186">
                    <span data-id="element-5187">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5188" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-5189">
              <h2 className="text-2xl font-bold mb-4" data-id="element-5190">
                Ready to write better product listings?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-5191">
                Apply these techniques to your digital products and start
                converting more browsers into buyers.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-5192">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5193" />
    </div>;
};